import {
  useContext,
  useState,
  useEffect,
  type ReactElement,
  FC,
  cloneElement,
  MutableRefObject,
} from "react";
import APIContext from "../contexts/APIContext";
import { SolutionClasses } from "../types/SolutionClasses";
import { MilestonesSectionConfigurations } from "../types/MilestonesSectionConfigurations";
import NotificationToast from "./notification-toast";
import {
  Box,
  Colors,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@analyticsMD/quil";
import Divider from "@mui/material/Divider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceholderSection from "./sections/placeholder-section";
import DischargePlanSection from "./sections/discharge-plan-section";
import ClinicalInsightsSection from "./sections/clinical-insights-section";
import InsightsSection from "./sections/insights-section";
import MilestonesSection from "./sections/milestones-section";
import ConsiderationSection from "./sections/consideration-section";
import PatientInfoSection from "./sections/patient-info-section";
import { ReactNode } from "react-markdown";
import NotesSection from "./sections/notes-section";

const TOAST_ERROR_MESSAGE =
  "We encountered an issue while updating patient data. Please try again";

const mainStyles = {
  columns: { display: "flex", flexDirection: "column" },
};

interface QCardSectionProps {
  useSidebarView?: boolean;
  updateRef: MutableRefObject<Record<string, any>>;
  closeButtonCmpt?: ReactNode;
}

const QCardSections: FC<QCardSectionProps> = ({
  useSidebarView = false,
  updateRef = null,
  closeButtonCmpt = null,
}) => {
  const [showDemographics, setShowDemographics] = useState(false);
  const {
    patientQuery,
    configurationQuery,
    showErrorToast,
    setShowErrorToast,
    isViewOnly,
  } = useContext(APIContext);

  const isEligible = patientQuery.data?.eligible_classes?.some((ec) =>
    [
      SolutionClasses.ACTIVE_UNIT,
      SolutionClasses.DISCHARGE_PLANNING,
      SolutionClasses.DISCHARGE_SUPPORT,
      SolutionClasses.OBSERVATION_PLANNING,
      SolutionClasses.OBSERVATION_SUPPORT,
    ].includes(ec)
  );

  const isWideLayout = useMediaQuery("(min-width:62.5rem)");
  const useWideLayout = useSidebarView ? false : isWideLayout;
  const urlParams = new URLSearchParams(window.location.search);
  const inRoundsMode =
    urlParams.get("emr_mode")?.toLowerCase() === "rounds" || useSidebarView;

  useEffect(() => {
    setShowDemographics(!inRoundsMode);
  }, [inRoundsMode]);

  if (!isEligible && !isViewOnly) {
    return <PlaceholderSection closeButtonCmpt={closeButtonCmpt} />;
  }

  const configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};

  const isMilestonesEnabled =
    configuration.milestones_section !==
    MilestonesSectionConfigurations.DISABLED;

  const insightsSectionEnabled = configuration.insights_section_enabled;
  const patientConsiderationEnabled =
    configuration.patient_consideration_enabled;
  const secondColumnEnabled =
    insightsSectionEnabled || patientConsiderationEnabled;

  const notesSectionEnabled = configuration.notes_section_enabled;

  const generateSections = () => {
    if (useWideLayout) {
      return (
        <Box sx={{ ...mainStyles.columns, background: Colors.grey_10 }}>
          <Grid container spacing={0.5} columns={secondColumnEnabled ? 2 : 1}>
            <Grid item xs={1}>
              <Stack spacing={0.5} sx={{ paddingBottom: "0.5rem" }}>
                <DischargePlanSection />
                {isMilestonesEnabled && (
                  <MilestonesSection configuration={configuration} />
                )}
                {notesSectionEnabled && <NotesSection />}
                <ClinicalInsightsSection configuration={configuration} />
              </Stack>
            </Grid>
            <Grid item xs={1}>
              <Stack spacing={0.5} sx={{ height: "100%" }}>
                {patientConsiderationEnabled && (
                  <ConsiderationSection configuration={configuration} />
                )}
                {insightsSectionEnabled && <InsightsSection />}
              </Stack>
            </Grid>
          </Grid>
          <NotificationToast
            message={TOAST_ERROR_MESSAGE}
            showToast={showErrorToast}
            isError={true}
            onClose={() => setShowErrorToast(false)}
          />
        </Box>
      );
    }
    const divider = <Divider sx={{ margin: "0.5rem 1rem" }} />;
    // Build section order dynamically based on rounding mode for cleaner implementation
    const sections: ReactElement[] = [
      <DischargePlanSection
        key="discharge"
        useSidebarView={useSidebarView}
        updateRef={updateRef}
      />,
      isMilestonesEnabled && (
        <MilestonesSection key="milestones" configuration={configuration} />
      ),
      notesSectionEnabled && <NotesSection key="notes" />,
      insightsSectionEnabled && <InsightsSection key="insights" />,
      patientConsiderationEnabled && (
        <ConsiderationSection
          key="consideration"
          configuration={configuration}
        />
      ),
      <ClinicalInsightsSection
        key="clinicalInsights"
        configuration={configuration}
      />,
    ].filter(Boolean) as ReactElement[]; // Remove any falsy entries

    // Interleave divider between sections
    const content = sections.flatMap((section, index) =>
      index === 0 ? section : [cloneElement(divider, { key: index }), section]
    );

    return (
      <Box
        sx={{
          position: "relative",
        }}
      >
        {content}
        <NotificationToast
          message={TOAST_ERROR_MESSAGE}
          showToast={showErrorToast}
          isError={true}
          onClose={() => setShowErrorToast(false)}
        />
      </Box>
    );
  };

  return (
    <>
      {isViewOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 1rem",
            backgroundColor: Colors.light_30,
            borderBottom: `1px solid ${Colors.grey_20}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "1rem", color: Colors.grey_40 }} />
          <Typography sx={{ fontSize: "0.812rem", color: Colors.grey_60 }}>
            This patient has been discharged. QCard is view-only.
          </Typography>
        </Box>
      )}
      <PatientInfoSection
        showRoundingMode={!isViewOnly}
        showDemographics={showDemographics}
        useSidebarView={useSidebarView}
        closeButtonCmpt={closeButtonCmpt}
      />
      {generateSections()}
    </>
  );
};

export default QCardSections;
